import { Participant } from 'livekit-client';
import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { ParticipantItem } from 'features/ParticipantItem';
import { Speaker } from 'features/ParticipantItem/presenters/Speaker';
import { useVideoQualityByParticipantCount } from 'hooks/useVideoQualityByParticipantCount';
import { useParticipantSubscriptions } from 'hooks/useParticipantSubscriptions';
import { STAGE_LAYOUT } from '../../../consts';
import { useSpeakerTrackQuality } from '../hooks/useSpeakerTrackQuality';
import { StageProps } from '../types';
import { StageFooter } from '../components/StageFooter';
import { ParticipantsList } from './components/ParticipantsList/ParticipantsList';
import { FooterLayout, MainLayout, SidebarLayout, StageLayout } from './view';

type SpeakerStageProps = StageProps & { participants: Participant[] };

export const SpeakerStage = observer(
    ({ roomState, controlRenderer, onLeave, hasRecorder, participants }: SpeakerStageProps) => {
        const [participantInFocus, ...otherParticipants] = participants;
        const isSidebarHidden = otherParticipants.length === 0;

        useVideoQualityByParticipantCount({
            isSidebarMode: false, // Спикер в главном окне, не в сайдбаре
            stageLayout: STAGE_LAYOUT.SPEAKER,
        });

        const focusParticipantList = useMemo(
            () => (participantInFocus ? [participantInFocus] : []),
            [participantInFocus],
        );

        // Для спикера в главном окне подписка не требует IntersectionObserver
        // но используем единый хук для консистентности
        useParticipantSubscriptions({
            room: roomState.room,
            participants: focusParticipantList,
            maxVisible: 1,
            speakerParticipantId: participantInFocus?.identity,
            isSidebarMode: true,
        });

        useSpeakerTrackQuality(participantInFocus);

        return (
            <StageLayout>
                <MainLayout $isWide={isSidebarHidden}>
                    <ParticipantItem
                        key={participantInFocus.identity}
                        height="100%"
                        width="100%"
                        participant={participantInFocus}
                        PresenterComponent={Speaker}
                        isHideSpeakingBorder
                    />
                </MainLayout>
                <SidebarLayout $hidden={isSidebarHidden}>
                    <ParticipantsList
                        participants={otherParticipants}
                        room={roomState.room}
                        speakerParticipantId={participantInFocus?.identity}
                        isHideSpeakingBorder
                    />
                </SidebarLayout>
                <FooterLayout>
                    {!hasRecorder && (
                        <StageFooter
                            roomState={roomState}
                            controlRenderer={controlRenderer}
                            onLeave={onLeave}
                            participants={participants}
                        />
                    )}
                </FooterLayout>
            </StageLayout>
        );
    },
);
